/**
 * Mock vitals generator.
 *
 * The real Oron app derives these values from a camera-based rPPG SDK. Here we
 * fabricate random-but-physiologically-plausible values so the web flow can
 * offer the same end-to-end experience without the native SDK.
 *
 * Field names mirror the native `VitalsData` type (src/features/vitals/types.ts).
 */

const rand = (min, max) => Math.random() * (max - min) + min;
const randInt = (min, max) => Math.round(rand(min, max));
const round1 = (n) => Math.round(n * 10) / 10;

/** Empty snapshot, used to seed the live-ticking display during capture. */
export const EMPTY_VITALS = {
  hr: 0,
  hrv_sdnn: 0,
  sbp: 0,
  dbp: 0,
  spo2: 0,
  stress: 0,
  wellness: 0,
  rr: 0,
  hemoglobin: 0,
  hba1c: 0,
};

/** Generate a full set of realistic vital-sign readings for a healthy adult. */
export const generateMockVitals = () => {
  const sbp = randInt(112, 132);
  // Keep diastolic sensibly below systolic.
  const dbp = randInt(72, Math.min(86, sbp - 32));

  return {
    hr: randInt(64, 88),
    hrv_sdnn: randInt(28, 66),
    sbp,
    dbp,
    spo2: randInt(96, 99),
    stress: round1(rand(1.2, 3.4)),
    wellness: round1(rand(7.2, 9.4)),
    rr: randInt(13, 19),
    hemoglobin: round1(rand(12.8, 16.2)),
    hba1c: round1(rand(5.0, 5.8)),
  };
};

/**
 * A partial, jittered snapshot used while "measuring" so the on-screen numbers
 * animate toward the final reading instead of appearing all at once.
 */
export const jitterTowards = (target, progress) => {
  // progress: 0 → 1. Blend from noisy values toward the target as we near 100%.
  const noise = (v, amt) => v + rand(-amt, amt) * (1 - progress);
  return {
    hr: Math.round(noise(target.hr, 12)),
    hrv_sdnn: Math.round(noise(target.hrv_sdnn, 18)),
    sbp: Math.round(noise(target.sbp, 10)),
    dbp: Math.round(noise(target.dbp, 8)),
    spo2: Math.min(100, Math.round(noise(target.spo2, 3))),
    stress: round1(noise(target.stress, 1.2)),
    wellness: round1(noise(target.wellness, 1.5)),
    rr: Math.round(noise(target.rr, 4)),
    hemoglobin: round1(noise(target.hemoglobin, 1.5)),
    hba1c: round1(noise(target.hba1c, 0.6)),
  };
};
